import { useEffect, useRef } from 'react';
import { DateStrip } from './sections/DateStrip';
import { HoursGrid } from './sections/HoursGrid';
import { ScheduleAlerts } from './sections/ScheduleAlerts';
import type { ScheduleStepProps } from './ScheduleStep.types';
import { useScheduleStep } from './useScheduleStep';
import {
  buildScheduleDisplayGroups,
  useScheduleSummary,
} from './scheduleSummaryContext';
import { normalizeDateKey } from './scheduleStepHelpers';

export function ScheduleStep(props: ScheduleStepProps) {
  const step = useScheduleStep(props);
  const missingDates = step.missingDates;
  const { registerApi } = useScheduleSummary();
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    const slots = props.submitData.bookingSlots || [];
    registerApi({
      displays: buildScheduleDisplayGroups(
        slots,
        undefined,
        props.billboardIds,
      ),
      onRemoveSlot: (_billboardId, date, hour) => {
        stepRef.current.removeSlot(date, hour);
      },
      onClearAll: () => stepRef.current.clearAll(),
    });
    return () => registerApi(null);
  }, [props.submitData.bookingSlots, props.billboardIds, registerApi]);

  return (
    <div className="camp-fade-up flex w-full flex-col gap-3">
      {props.embedded ? null : (
        <header className="text-left">
          <h1 className="m-0 text-[18px] font-semibold tracking-tight text-[#1F2433]">
            Schedule &amp; Slots
          </h1>
          <p className="mt-0.5 mb-0 max-w-2xl text-[12.5px] leading-snug text-[#6B7289]">
            Pick a date, tap hours to book, then set plays. This plan applies to
            every selected display.
          </p>
        </header>
      )}

      <ScheduleAlerts
        showExpired={Boolean(props.slotsHoldExpired) && step.selectedCount === 0}
        allDatesCount={step.allDates.length}
        missingDatesCount={missingDates.length}
        needsAllDates={false}
      />

      <DateStrip
        dates={step.dates}
        allDatesCount={step.allDates.length}
        showRangeNote={step.showRangeNote}
        activeDate={step.activeDate}
        onSelectDate={step.setActiveDate}
        bookingSlots={props.submitData.bookingSlots}
      />

      <HoursGrid
        activeLabel={step.activeLabel}
        isLoadingSlots={step.isLoadingSlots}
        activeDayCount={
          props.submitData.bookingSlots.filter(
            (s) =>
              normalizeDateKey(s.date) === normalizeDateKey(step.activeDate),
          ).length
        }
        hours={step.hours}
        hideApplyToAll={true}
        activeDate={step.activeDate}
        defaultTotalReps={step.defaultTotalReps}
        isSelected={step.isSelected}
        getSlot={step.getSlot}
        onToggle={step.toggleSlot}
        onSetReps={step.setReps}
        syncRepsAllDates={step.syncRepsAllDates}
        setSyncRepsAllDates={step.setSyncRepsAllDates}
      />
    </div>
  );
}
